import React, {useEffect, useMemo, useState} from "react";
import {Button, Form, Modal, Spinner, Table} from "react-bootstrap";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import useAuth from "../../../../hooks/useAuth";
import {toastError, toastSuccess} from "../../../../helpers/Toast";

const HOURLY = "HOURLY";
const MONTHLY = "MONTHLY";
const today = () => new Date().toISOString().slice(0, 10);
const emptyForm = () => ({
  id: null,
  validFrom: today(),
  validTo: "",
  billingType: HOURLY,
  hourlyRate: "",
  netHourlyRate: "",
  monthlyRate: "",
  netMonthlyRate: "",
  notes: "",
  active: true,
  version: null
});

const rolesArray = roles => Array.isArray(roles)
  ? roles
  : typeof roles === "string"
    ? roles.replace(/[\[\]\s]/g, "").split(",").filter(Boolean)
    : [];

const money = value => value === null || value === undefined
  ? "—"
  : `${Number(value).toLocaleString("pl-PL", {minimumFractionDigits: 2, maximumFractionDigits: 2})} zł`;
const percent = value => value === null || value === undefined
  ? "—"
  : `${Number(value).toLocaleString("pl-PL", {maximumFractionDigits: 4})}%`;

const sourceName = value => ({
  MANUAL: "Ręczna stawka zmiany",
  EMPLOYEE_POSITION_PERIOD: "Stawka okresowa pracownik + stanowisko",
  EMPLOYEE_POSITION: "Stawka przypisana pracownikowi na stanowisku",
  CONTRACT: "Stawka godzinowa z umowy",
  CONTRACT_MONTHLY: "Stawka miesięczna z umowy / norma",
  POSITION: "Stawka podstawowa stanowiska"
}[value] || value || "—");

const profileName = value => ({
  STUDENT_PUPIL: "Uczeń / student < 26 lat (umowa cywilnoprawna)",
  KRUS: "KRUS",
  STANDARD_ZUS: "Standardowy ZUS",
  NO_ZUS: "Bez składek pracodawcy",
  UNKNOWN: "Profil nieustalony"
}[value] || value || "—");

const billingName = value => String(value || HOURLY).toUpperCase() === MONTHLY ? "Miesięczna" : "Godzinowa";

const getError = error => error?.response?.data?.message
  || (typeof error?.response?.data === "string" ? error.response.data : null)
  || "Operacja nie powiodła się.";

const isActiveOn = (rate, date) => {
  if (!rate?.active || !date) return false;
  if (rate.validFrom && rate.validFrom > date) return false;
  if (rate.validTo && rate.validTo < date) return false;
  return true;
};

const EmployeePositionRatesModal = ({
  show,
  onHide,
  employeeId,
  employeeName,
  positionId,
  positionName,
  legacyRate,
  legacyNetRate,
  legacyBillingType,
  legacyMonthlyRate,
  legacyNetMonthlyRate
}) => {
  const axiosPrivate = useAxiosPrivate();
  const {auth} = useAuth();
  const roles = useMemo(() => rolesArray(auth?.roles), [auth?.roles]);
  const canEdit = roles.includes("ROLE_HR") || roles.includes("ROLE_ADMIN");

  const [rates, setRates] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewDate, setPreviewDate] = useState(today());
  const [cost, setCost] = useState(null);
  const [costLoading, setCostLoading] = useState(false);

  const loadRates = async () => {
    if (!employeeId || !positionId) return;
    setLoading(true);
    try {
      const response = await axiosPrivate.get(`/employee-position-rates/employee/${employeeId}/position/${positionId}`);
      setRates(response.data || []);
    } catch (error) {
      setRates([]);
      toastError(getError(error));
    } finally {
      setLoading(false);
    }
  };

  const loadCost = async date => {
    if (!employeeId || !positionId || !date) return;
    setCostLoading(true);
    try {
      const response = await axiosPrivate.get(`/labor-costs/employee/${employeeId}/position/${positionId}`, {params: {date}});
      setCost(response.data || null);
    } catch (error) {
      setCost(null);
      toastError(getError(error));
    } finally {
      setCostLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    setForm(emptyForm());
    loadRates();
    loadCost(previewDate);
  }, [show, employeeId, positionId]);

  const activePreviewRate = useMemo(
    () => rates.find(rate => isActiveOn(rate, previewDate)) || null,
    [rates, previewDate]
  );

  const baseBillingType = String(legacyBillingType || (legacyMonthlyRate ? MONTHLY : HOURLY)).toUpperCase();
  const baseGross = baseBillingType === MONTHLY ? legacyMonthlyRate : legacyRate;
  const baseNet = baseBillingType === MONTHLY ? legacyNetMonthlyRate : legacyNetRate;
  const baseUnit = baseBillingType === MONTHLY ? "mies." : "h";

  const change = event => {
    const {name, value, type, checked} = event.target;
    setForm(current => ({...current, [name]: type === "checkbox" ? checked : value}));
  };

  const changeBillingType = event => {
    const value = event.target.value;
    setForm(current => ({
      ...current,
      billingType: value,
      hourlyRate: value === HOURLY ? current.hourlyRate : "",
      netHourlyRate: value === HOURLY ? current.netHourlyRate : "",
      monthlyRate: value === MONTHLY ? current.monthlyRate : "",
      netMonthlyRate: value === MONTHLY ? current.netMonthlyRate : ""
    }));
  };

  const reset = () => setForm(emptyForm());

  const edit = rate => {
    const billingType = String(rate.billingType || HOURLY).toUpperCase();
    setForm({
      id: rate.id,
      validFrom: rate.validFrom || today(),
      validTo: rate.validTo || "",
      billingType,
      hourlyRate: billingType === HOURLY ? (rate.hourlyRate ?? "") : "",
      netHourlyRate: billingType === HOURLY ? (rate.netHourlyRate ?? "") : "",
      monthlyRate: billingType === MONTHLY ? (rate.monthlyRate ?? "") : "",
      netMonthlyRate: billingType === MONTHLY ? (rate.netMonthlyRate ?? "") : "",
      notes: rate.notes || "",
      active: rate.active !== false,
      version: rate.version ?? null
    });
  };

  const save = async () => {
    if (!canEdit) return;
    if (!form.validFrom) return toastError("Podaj datę początku obowiązywania stawki.");
    if (form.validTo && form.validTo < form.validFrom) return toastError("Data końca nie może być wcześniejsza niż data początku.");

    if (form.billingType === HOURLY) {
      if (form.hourlyRate === "" || Number(form.hourlyRate) <= 0) return toastError("Podaj stawkę godzinową brutto większą od zera.");
      if (form.netHourlyRate !== "" && Number(form.netHourlyRate) < 0) return toastError("Podaj prawidłową stawkę godzinową netto.");
    } else {
      if (form.monthlyRate === "" || Number(form.monthlyRate) <= 0) return toastError("Podaj stawkę miesięczną brutto większą od zera.");
      if (form.netMonthlyRate !== "" && Number(form.netMonthlyRate) < 0) return toastError("Podaj prawidłową stawkę miesięczną netto.");
    }

    const payload = {
      employeeId,
      positionId,
      validFrom: form.validFrom,
      validTo: form.validTo || null,
      billingType: form.billingType,
      hourlyRate: form.billingType === HOURLY ? Number(form.hourlyRate) : null,
      netHourlyRate: form.billingType === HOURLY && form.netHourlyRate !== "" ? Number(form.netHourlyRate) : null,
      monthlyRate: form.billingType === MONTHLY ? Number(form.monthlyRate) : null,
      netMonthlyRate: form.billingType === MONTHLY && form.netMonthlyRate !== "" ? Number(form.netMonthlyRate) : null,
      notes: form.notes?.trim() || null,
      active: !!form.active,
      version: form.version
    };

    setSaving(true);
    try {
      if (form.id) {
        await axiosPrivate.put(`/employee-position-rates/${form.id}`, payload);
        toastSuccess("Zaktualizowano stawkę okresową.");
      } else {
        await axiosPrivate.post("/employee-position-rates", payload);
        toastSuccess("Dodano stawkę okresową.");
      }
      reset();
      await loadRates();
      await loadCost(previewDate);
    } catch (error) {
      toastError(getError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async rate => {
    const rateValue = String(rate.billingType || HOURLY).toUpperCase() === MONTHLY ? rate.monthlyRate : rate.hourlyRate;
    const unit = String(rate.billingType || HOURLY).toUpperCase() === MONTHLY ? "mies." : "h";
    if (!canEdit || !window.confirm(`Usunąć stawkę ${money(rateValue)}/${unit} obowiązującą od ${rate.validFrom}?`)) return;
    try {
      await axiosPrivate.delete(`/employee-position-rates/${rate.id}`);
      toastSuccess("Usunięto stawkę okresową.");
      if (form.id === rate.id) reset();
      await loadRates();
      await loadCost(previewDate);
    } catch (error) {
      toastError(getError(error));
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Stawki i koszt — {employeeName || `pracownik #${employeeId}`} / {positionName || `stanowisko #${positionId}`}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="alert alert-light border py-2 mb-3">
          Stawka okresowa ma pierwszeństwo przed stawką bazową przypisaną pracownikowi na stanowisku
          {baseGross !== null && baseGross !== undefined && baseGross !== "" ? ` (${money(baseGross)}/${baseUnit} brutto${baseNet !== null && baseNet !== undefined && baseNet !== "" ? `, ${money(baseNet)}/${baseUnit} netto` : ""})` : ""},
          a następnie przed mechanizmami zapasowymi. Okresowa stawka może być godzinowa albo miesięczna. Dla stawki miesięcznej system wylicza koszt jednej godziny z normy czasu pracy właściwej dla miesiąca kalkulacji. Aktywne okresy nie mogą się nakładać.
        </div>

        <div className="row g-3">
          <div className="col-lg-7">
            <h6>Historia stawek</h6>
            {loading ? <Spinner animation="border" size="sm" /> : (
              <div className="table-responsive">
                <Table size="sm" bordered hover className="align-middle">
                  <thead><tr><th>Od</th><th>Do</th><th>Forma</th><th>Brutto</th><th>Netto</th><th>Aktywna</th><th>Uwagi</th>{canEdit && <th>Akcje</th>}</tr></thead>
                  <tbody>
                    {rates.length === 0 && <tr><td colSpan={canEdit ? 8 : 7} className="text-center text-muted">Brak stawek okresowych. Obowiązuje stawka bazowa przypisana do stanowiska lub mechanizm zapasowy.</td></tr>}
                    {rates.map(rate => {
                      const type = String(rate.billingType || HOURLY).toUpperCase();
                      const gross = type === MONTHLY ? rate.monthlyRate : rate.hourlyRate;
                      const net = type === MONTHLY ? rate.netMonthlyRate : rate.netHourlyRate;
                      const unit = type === MONTHLY ? "mies." : "h";
                      return (
                        <tr key={rate.id}>
                          <td>{rate.validFrom}</td>
                          <td>{rate.validTo || "bez końca"}</td>
                          <td>{billingName(type)}</td>
                          <td>{money(gross)}/{unit}</td>
                          <td>{money(net)}/{unit}</td>
                          <td>{rate.active ? "TAK" : "NIE"}</td>
                          <td>{rate.notes || "—"}</td>
                          {canEdit && <td className="text-nowrap"><Button size="sm" variant="outline-primary" onClick={() => edit(rate)}>Edytuj</Button>{" "}<Button size="sm" variant="outline-danger" onClick={() => remove(rate)}>Usuń</Button></td>}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            )}

            {canEdit && <div className="border rounded p-3 mt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">{form.id ? "Edycja stawki" : "Nowa stawka okresowa"}</h6>
                {form.id && <Button size="sm" variant="outline-secondary" onClick={reset}>Nowa</Button>}
              </div>
              <div className="row g-2">
                <Form.Group className="col-md-3"><Form.Label>Od *</Form.Label><Form.Control name="validFrom" type="date" value={form.validFrom} onChange={change}/></Form.Group>
                <Form.Group className="col-md-3"><Form.Label>Do</Form.Label><Form.Control name="validTo" type="date" value={form.validTo} onChange={change}/></Form.Group>
                <Form.Group className="col-md-3"><Form.Label>Forma *</Form.Label><Form.Select name="billingType" value={form.billingType} onChange={changeBillingType}><option value={HOURLY}>Godzinowa</option><option value={MONTHLY}>Miesięczna</option></Form.Select></Form.Group>
                <Form.Group className="col-md-3 d-flex align-items-end pb-2"><Form.Check name="active" checked={form.active} onChange={change} label="Aktywna"/></Form.Group>
                {form.billingType === HOURLY ? <>
                  <Form.Group className="col-md-3"><Form.Label>Brutto/h *</Form.Label><Form.Control name="hourlyRate" type="number" min="0.01" step="0.01" value={form.hourlyRate} onChange={change}/></Form.Group>
                  <Form.Group className="col-md-3"><Form.Label>Netto/h</Form.Label><Form.Control name="netHourlyRate" type="number" min="0" step="0.01" value={form.netHourlyRate} onChange={change}/></Form.Group>
                </> : <>
                  <Form.Group className="col-md-3"><Form.Label>Brutto/mies. *</Form.Label><Form.Control name="monthlyRate" type="number" min="0.01" step="0.01" value={form.monthlyRate} onChange={change}/></Form.Group>
                  <Form.Group className="col-md-3"><Form.Label>Netto/mies.</Form.Label><Form.Control name="netMonthlyRate" type="number" min="0" step="0.01" value={form.netMonthlyRate} onChange={change}/></Form.Group>
                </>}
                <Form.Group className="col-12"><Form.Label>Uwagi</Form.Label><Form.Control name="notes" value={form.notes} onChange={change}/></Form.Group>
              </div>
              <div className="mt-3"><Button onClick={save} disabled={saving}>{saving ? "Zapisywanie..." : (form.id ? "Zapisz zmiany" : "Dodaj stawkę")}</Button></div>
            </div>}
          </div>

          <div className="col-lg-5">
            <div className="border rounded p-3">
              <div className="d-flex gap-2 align-items-end mb-3">
                <Form.Group className="flex-grow-1"><Form.Label>Kalkulacja kosztu na dzień</Form.Label><Form.Control type="date" value={previewDate} onChange={event => setPreviewDate(event.target.value)}/></Form.Group>
                <Button variant="outline-primary" onClick={() => loadCost(previewDate)} disabled={costLoading}>{costLoading ? "..." : "Przelicz"}</Button>
              </div>
              {!cost ? <div className="text-muted">Brak kalkulacji.</div> : <>
                <Table size="sm" borderless className="mb-2"><tbody>
                  <tr><td>Stawka brutto</td><td className="text-end"><strong>{money(cost.wageRate)}/h</strong></td></tr>
                  <tr><td>Stawka netto</td><td className="text-end"><strong>{money(cost.netWageRate)}/h</strong></td></tr>
                  <tr><td>Źródło stawki</td><td className="text-end">{sourceName(cost.wageRateSource)}</td></tr>
                  <tr><td>Profil ubezpieczeniowy</td><td className="text-end">{profileName(cost.insuranceProfile)}</td></tr>
                  <tr><td>Składki pracodawcy</td><td className="text-end">{percent(cost.employerContributionPercent)} / {money(cost.employerContributionsHourly)}/h</td></tr>
                  <tr><td>Prowizja agencji</td><td className="text-end">{percent(cost.agencyCommissionPercent)} / {money(cost.agencyCommissionHourly)}/h</td></tr>
                  <tr className="border-top"><td><strong>Całkowity koszt pracodawcy</strong></td><td className="text-end"><strong>{money(cost.totalHourlyCost)}/h</strong></td></tr>
                </tbody></Table>
                <div className="small text-muted">
                  {activePreviewRate && String(activePreviewRate.billingType || HOURLY).toUpperCase() === MONTHLY && <div>Aktywna stawka okresowa: {money(activePreviewRate.monthlyRate)}/mies. brutto{activePreviewRate.netMonthlyRate != null ? `, ${money(activePreviewRate.netMonthlyRate)}/mies. netto` : ""}</div>}
                  {cost.hiringCompanyName && <div>Firma/agencja: {cost.hiringCompanyName}</div>}
                  {cost.contractIdentifier && <div>Umowa: {cost.contractIdentifier}{cost.contractBillingType ? ` (${cost.contractBillingType})` : ""}</div>}
                  {cost.contractMonthlyRate != null && <div>Kwota miesięczna z umowy brutto: {money(cost.contractMonthlyRate)}; netto: {money(cost.contractNetMonthlyRate)}</div>}
                  {cost.monthlyNormHours != null && <div>Norma miesiąca użyta do przeliczenia: {cost.monthlyNormHours} h</div>}
                  {(cost.rateValidFrom || cost.rateValidTo) && <div>Okres źródła stawki: {cost.rateValidFrom || "—"} — {cost.rateValidTo || "bez końca"}</div>}
                  <div>Kompletność kosztu: <strong>{cost.complete ? "TAK" : "NIE"}</strong></div>
                </div>
                {(cost.warnings || []).length > 0 && <div className="alert alert-warning mt-2 mb-0 py-2"><strong>Uwagi:</strong><ul className="mb-0">{cost.warnings.map((warning, index) => <li key={index}>{warning}</li>)}</ul></div>}
              </>}
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" onClick={onHide}>Zamknij</Button></Modal.Footer>
    </Modal>
  );
};

export default EmployeePositionRatesModal;
